import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import MagnetCursor from "../components/MagnetCursor";
import Preloader from "../components/Preloader";
import ScrollToTop from "../components/ScrollToTop";

const AAER_URL = "https://www.aaerlaw.com/";

const capabilities = [
  {
    icon: "ti ti-search",
    title: "Legal research in one place",
    text: "Explore reported judgments, up-to-date laws, precedents, and reference materials through a focused legal research experience.",
  },
  {
    icon: "ti ti-file-description",
    title: "Contract management",
    text: "Work with practical contract tools and detailed precedents designed to make drafting and contract administration more efficient.",
  },
  {
    icon: "ti ti-books",
    title: "Continuing legal education",
    text: "Connect with articles, journals, textbooks, and learning materials from legal minds and institutions.",
  },
  {
    icon: "ti ti-headphones",
    title: "Accessible, modern research",
    text: "Use fast search, speech-enabled reading, and an intuitive interface built to support focused work across long research sessions.",
  },
];

function AaerLegalTechnology() {
  useEffect(() => {
    document.title = "AAER Legal Technology | Paraclearth";
    const preloader = document.getElementById("preloader");
    if (preloader) preloader.style.display = "none";
  }, []);

  return (
    <div className="aaer-page">
      <Preloader />

      <section className="aaer-page-hero">
        <div className="aaer-page-orbit aaer-page-orbit-one"></div>
        <div className="aaer-page-orbit aaer-page-orbit-two"></div>
        <div className="container position-relative">
          <div className="row">
            <div className="col-12 col-xl-10">
              <div className="aaer-kicker aaer-kicker-light">
                Technology for legal practice
              </div>
              <h1>All the law practice tools you need, connected through AAER.</h1>
              <p>
                All Appeal Electronic Reports is a legal technology platform
                designed to bring research, practice resources, contract tools,
                and professional learning into one accessible environment.
              </p>
              <div className="d-flex flex-wrap gap-3 mt-4">
                <a
                  className="btn btn-primary"
                  href={AAER_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>
                    Visit AAER <i className="ti ti-external-link"></i>
                  </span>
                  <span>
                    Visit AAER <i className="ti ti-external-link"></i>
                  </span>
                </a>
                <Link className="aaer-back-link" to="/">
                  <i className="ti ti-arrow-left"></i> Back to Paraclearth
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="aaer-story-section bg-white">
        <div className="container">
          <div className="row g-5 align-items-start">
            <div className="col-12 col-lg-5">
              <div className="section-heading aaer-sticky-heading">
                <div className="sub-title">
                  <img src="assets/img/core-img/hammer.png" alt="" />
                  About AAER
                </div>
                <h2>Built around the realities of legal work.</h2>
              </div>
            </div>
            <div className="col-12 col-lg-7">
              <div className="aaer-story-copy">
                <p className="aaer-lead">
                  AAER stands for All Appeal Electronic Reports. It developed
                  from Abuja Appeal Quarterly Reports, a law reporting
                  initiative unveiled in 2019.
                </p>
                <p>
                  Today, AAER extends beyond electronic law reports. It is
                  positioned as a practical workspace where lawyers, legal
                  teams, researchers, students, and institutions can find
                  authoritative resources and tools that support everyday legal
                  practice.
                </p>
                <p>
                  For Paraclearth visitors, AAER offers a direct path from legal
                  advice and representation to the wider research and practice
                  resources that help people understand and work with the law.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="aaer-capabilities-section">
        <div className="container">
          <div className="row justify-content-between align-items-end mb-5 g-4">
            <div className="col-12 col-lg-7">
              <div className="aaer-kicker">What the platform brings together</div>
              <h2>A more connected way to research, draft, and keep learning.</h2>
            </div>
            <div className="col-12 col-lg-4">
              <p className="mb-0">
                AAER combines core legal content with tools designed to reduce
                friction across research and practice workflows.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {capabilities.map((item, index) => (
              <div className="col-12 col-md-6" key={item.title}>
                <article className="aaer-capability-card">
                  <div className="aaer-capability-number">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <i className={item.icon}></i>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="aaer-audience-section bg-white">
        <div className="container">
          <div className="aaer-audience-panel">
            <div className="row align-items-center g-5">
              <div className="col-12 col-lg-6">
                <div className="aaer-kicker">Who AAER is for</div>
                <h2>Useful across the legal ecosystem.</h2>
                <p>
                  Whether you are preparing a matter, developing an argument,
                  drafting an agreement, studying the law, or building your
                  professional knowledge, AAER is designed to keep the resources
                  you need within reach.
                </p>
              </div>
              <div className="col-12 col-lg-6">
                <div className="aaer-audience-list">
                  <span>Legal practitioners</span>
                  <span>In-house legal teams</span>
                  <span>Researchers and academics</span>
                  <span>Law students</span>
                  <span>Courts and institutions</span>
                  <span>Businesses managing contracts</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="aaer-final-cta">
        <div className="container text-center">
          <div className="aaer-kicker aaer-kicker-light justify-content-center">
            Continue to AAER
          </div>
          <h2>See the platform and explore its legal resources.</h2>
          <p>
            Visit the official AAER website to learn more about its features,
            access options, and legal practice tools.
          </p>
          <a
            className="btn btn-primary"
            href={AAER_URL}
            target="_blank"
            rel="noreferrer"
          >
            <span>
              Explore aaerlaw.com <i className="ti ti-external-link"></i>
            </span>
            <span>
              Explore aaerlaw.com <i className="ti ti-external-link"></i>
            </span>
          </a>
        </div>
      </section>

      <ScrollToTop />
      <MagnetCursor />
    </div>
  );
}

export default AaerLegalTechnology;
